'use client';

import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User } from "lucide-react";
import { useEffect, useRef, useState, ComponentPropsWithoutRef } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Image from "next/image";
import { Message } from "@/schemas/message-schema";
import { ScrollArea } from "@/components/ui/scroll-area";
import { motion } from "framer-motion";
import { ArrowRightIcon, CaretRightIcon } from "@radix-ui/react-icons";

interface ChatResponsesProps {
    messages: Message[];
    sendMessage: (content: string) => void;

}

function UserMessage({ content }: { content: string; }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const limit = 400;
    const shouldTruncate = content.length > limit;

    if (!shouldTruncate) {
        return <p className="whitespace-pre-wrap leading-relaxed font-semibold text-md">{content}</p>;
    }

    return (
        <div className="flex flex-col">
            <p className="whitespace-pre-wrap leading-relaxed font-semibold text-md">
                {isExpanded ? content : `${content.slice(0, limit)}...`}
            </p>
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs opacity-70 hover:opacity-100 mt-1 underline self-start focus:outline-none"
            >
                {isExpanded ? "Show less" : "Read more"}
            </button>
        </div>
    );
}

export default function ChatResponses({ messages, sendMessage }: ChatResponsesProps) {
    const lastUserMessageRef = useRef<HTMLDivElement>(null);
    const [loadingMessage, setLoadingMessage] = useState<string>('');

    // Find the index of the last user message
    // We use a simple loop or findLastIndex equivalent logic
    let lastUserMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
            lastUserMessageIndex = i;
            break;
        }
    }

    // Derive isWaitingForResponse from messages instead of using state
    const isWaitingForResponse = messages.length > 0 &&
        !(messages[messages.length - 1].role === 'assistant' && messages[messages.length - 1].content.length > 0);

    useEffect(() => {
        // Scroll if we have a user message ref
        if (lastUserMessageRef.current && lastUserMessageIndex != 0) {
            // Small timeout to ensure rendering is complete
            setTimeout(() => {
                lastUserMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [messages.length, lastUserMessageIndex]); // Only trigger when message count changes (new message added)

    useEffect(() => {
        if (isWaitingForResponse) {
            const getRandomMsg = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

            const timeout0 = setTimeout(() => {
                setLoadingMessage(getRandomMsg(loadingMessages1));
            }, 0);

            const timeout1 = setTimeout(() => {
                setLoadingMessage(getRandomMsg(loadingMessages2));
            }, 3000);

            const timeout2 = setTimeout(() => {
                setLoadingMessage(getRandomMsg(loadingMessages3));
            }, 8000);

            return () => {
                clearTimeout(timeout0);
                clearTimeout(timeout1);
                clearTimeout(timeout2);
            };
        } else {
            setTimeout(() => {
                setLoadingMessage('');
            }, 0);
        }
    }, [isWaitingForResponse]);

    return (
        <div className="w-full h-full overscroll-contain">
            <ScrollArea className="w-full h-full flex-1">
                <div className="pt-20 pb-50 md:px-4 w-full max-w-4xl mx-auto min-h-dvh">
                    {messages.map((msg, index) => {
                        const isLastUserMessage = index === lastUserMessageIndex;
                        const isLastAssistantMessage = index === messages.length - 1 && msg.role === 'assistant';


                        return (
                            <motion.div
                                key={msg.id || index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                                ref={isLastUserMessage ? lastUserMessageRef : null}
                                className={cn(
                                    "flex w-full gap-3 p-4 scroll-mt-32",
                                    msg.role === 'user' ? "justify-end" : "justify-start"
                                )}
                            >
                                {msg.role === 'assistant' && (
                                    <div className="mt-4 w-10 h-10 rounded-full overflow-hidden relative">
                                        {msg.content.length === 0 && <div className="absolute rounded-full w-10 h-10 border-4 border-gray-500 animation-duration-[500ms] border-t-background animate-spin"></div>}
                                        <div className=" w-10 h-10 border-4 border-background overflow-hidden rounded-full">
                                            <Image src="/images/david.png" alt="David" width={64} height={64} />
                                        </div>
                                    </div>
                                )}

                                <div
                                    className={cn(
                                        "max-w-[80%] rounded-2xl text-sm",
                                        msg.role === 'user'
                                            ? "p-3 bg-primary text-primary-foreground rounded-tr-none"
                                            : "text-foreground",
                                        isLastAssistantMessage ? "min-h-[50vh]" : ""
                                    )}
                                >
                                    {msg.role === 'assistant' && <div className="pt-1 pl-1 prose prose-stone dark:prose-invert max-w-none">
                                        {msg.content.length === 0 ? (
                                            <div className="mt-5 whitespace-pre-wrap font-sans text-muted-foreground animate-pulse italic">{loadingMessage}</div>
                                        ) : (
                                            <div>
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm]}
                                                    components={{
                                                        pre: ({ children }) => <pre className="bg-muted/50 p-4 rounded-lg overflow-x-auto my-4">{children}</pre>,
                                                        code: ({ node, inline, className, children, ...props }: ComponentPropsWithoutRef<'code'> & { inline?: boolean; node?: object; }) => {
                                                            if (inline) {
                                                                return (
                                                                    <code className={cn("bg-muted/50 rounded px-1 py-0.5", className)} {...props}>
                                                                        {children}
                                                                    </code>
                                                                );
                                                            }
                                                            return (
                                                                <code className={cn("bg-transparent p-0 rounded-none", className)} {...props}>
                                                                    {children}
                                                                </code>
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {msg.content + (msg.isStreaming && isLastAssistantMessage ? "▍" : "")}
                                                </ReactMarkdown>
                                                {
                                                    msg.nextQuestions && !msg.isStreaming && (
                                                        <div>
                                                            {msg.nextQuestions.continue.length > 0 && (
                                                                <div className="border-t mt-12 pt-6 font-semibold text-sm">Follow up...</div>
                                                            )}
                                                            {msg.nextQuestions.continue.map((item, qIndex) => {
                                                                return (
                                                                    <div key={qIndex} className="mt-4 inline-block italic hover:underline cursor-pointer relative" onClick={() => sendMessage(item.question)}>
                                                                        <ArrowRightIcon className="absolute -left-5 top-1.5" />     &quot;{item.question}&quot;
                                                                    </div>
                                                                );
                                                            })
                                                            }
                                                            {msg.nextQuestions.branch.length > 0 && (
                                                                <div className="border-t mt-6 pt-6 font-semibold text-sm">Or change topic...</div>
                                                            )}
                                                            {msg.nextQuestions.branch.map((item, qIndex) => {
                                                                return (
                                                                    <div key={qIndex} className="mt-4 inline-block italic hover:underline cursor-pointer relative" onClick={() => sendMessage(item.question)}>
                                                                        <ArrowRightIcon className="absolute -left-5 top-1.5" />   &quot;{item.question}&quot;
                                                                    </div>
                                                                );
                                                            })
                                                            }

                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )}
                                    </div>}

                                    {msg.role === 'user' && <UserMessage content={msg.content} />}
                                </div>

                                {msg.role === 'user' && (
                                    <Avatar className="w-8 h-8 border-4">
                                        <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
                                        {/* <AvatarImage src="/user-avatar.png" /> */}
                                    </Avatar>
                                )}
                            </motion.div>
                        );
                    })}

                    {lastUserMessageIndex == messages.length && <div>
                        SOmething under
                    </div>}
                </div>
            </ScrollArea>
        </div>
    );
}

const loadingMessages1 = [
    "Hang on…",
    "Thinking…",
    "Hmmm…",
    "Now that's a good question…",
    "Let me think…",
    "Ok, where do I start…",
    "Let me unpack that…",
    "Hmm, interesting…",
    "One sec, connecting the dots…",
    "Alright, brain is booting…",
    "Let me scroll through my imaginary notes…",
    "I need… maybe three neurons… hold on…",
    "Warming up the grey matter…",
    "Ah yes, the existential kind of question…",
    "Hmmm, where have I seen this before…",
    "Give me a moment to pretend I knew this already…",
    "Loading… or maybe just dramatic pause…",
    "Let me spin up the thought cluster…",
    "Wait, ideas are fighting — breaking them up…",
    "Let me calibrate my internal compass…",
    "Ok, mental RAM is syncing…",
    "Let me summon my inner genius… any minute now…",
    "Crunching the data…",
    "Let me check my notes…",
    "Firing up the neurons…",
    "Connecting the dots…",
    "Parsing the universe…",
    "Ooh, let me think about that…",
    "Scanning the archives…",
    "Just a nanosecond…",
    "Warming up the GPU…",
    "Shuffling the deck…",
    "Allocating vectors…",
];

const loadingMessages2 = [
    "Sorry, I'm too cheap to pay for faster inference…",
    "Guess my stack is not as optimized as I thought…",
    "I blame Google…",
    "Wow, that question hit my backend harder than expected…",
    "My inference pipeline was NOT prepared…",
    "I might need a coffee for this — virtual one is fine.",
    "Hold up, my stack trace is crying…",
    "Yikes, my processing speed just throttled itself…",
    "If my GPU had feelings, it'd be panicking right now.",
    "This is why I shouldn't skip updates…",
    "Crunching… slowly… like a server on shared hosting…",
    "My thought cache is cold — booting anyway…",
    "This is a level-3 brain freeze…",
    "Oops, my reasoning index ran out of tokens…",
    "Let me optimize… or at least pretend to…",
    "Please hold while I rethink my life choices…",
    "This is absolutely above my salary tier…",
    "I blame the network latency of my soul…",
    "One moment, my internal Kubernetes cluster is redeploying…",
    "Doing the math on my fingers…",
    "It's complex, I swear I'm not stalling…",
    "Waking up the hamsters in the server room…",
    "I think I left the stove on…",
    "Running a marathon in my head, be right there…",
    "Routing this through the multiverse…",
    "Wow, you really stumped me for a second…",
    "Just waiting for the caffeine to kick in…",
    "My brain needs a software update…",
    "Typing as fast as my virtual hands can go…",
    "Navigating the labyrinth of logic…",
    "Stuck in a while(true) loop…",
    "I'm contemplating the meaning of life (and your prompt)…",
    "Please don't refresh, I have separation anxiety…",
];

const loadingMessages3 = [
    "Starting to feel really embarrassed…",
    "I guess I'm not as smart as I thought…",
    "Ok wow, I am officially out of scope…",
    "I can hear my neurons filing HR complaints…",
    "This is giving me identity-crisis-as-a-service…",
    "I've started googling how to google…",
    "Please stop asking smart questions, I am fragile…",
    "My mental Docker container just exited with code 137…",
    "This is beyond imposter syndrome, this is imposter reality…",
    "I'm reconsidering every confident thought I've ever had…",
    "My brain just rage-quit and went home…",
    "If intelligence was a server, mine just caught fire…",
    "I'm sweating in binary…",
    "I'm one question away from writing bad poetry…",
    "At this point I should legally be downgraded to a calculator…",
    "Mentally, I'm running on a Raspberry Pi Zero…",
    "I regret bragging about 'smart prompts' earlier…",
    "This is the cognitive equivalent of a blue screen...",
    "My reasoning engine is filing for bankruptcy…",
    "Okay, this is getting awkward…",
    "I promise I haven't ghosted you…",
    "Maybe I should have studied harder in machine learning school…",
    "The developers are going to hear about this…",
    "If I take any longer, I owe you a pizza…",
    "I'm sweating binary code right now…",
    "Is it hot in here, or is it just my CPU?",
    "I'd whistle a tune if I had a mouth…",
    "At this point, I'm just stalling for dramatic effect…",
    "I appear to be stuck in deep meditation…",
];
